import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { startsAtText } from '../onboardingSupport/slaUtils';
import {
  ArrowDown,
  ArrowUp,
  CalendarCog,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Star,
  StickyNote,
  Trash2,
  Video,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import useAnchoredPopover from '../onboardingSupport/useAnchoredPopover';
import { useFetchMeetings } from '../../../services/onboardingBoard';
import { LegacyTag, OutlookTag, isLegacy, isOutlook, joinable } from '../scheduling/MeetingActions';

// The desk (Nic 2026-10-21): the Onboarding Workspace's Clients tab and
// Sales' Signing Up share one look. Counters that are filters, one list to
// work row by row, and a rail with the Schedule and Today (the page's
// suggestions plus their own to-dos). Each desk hands these pieces its own
// people: `personId` is an onboardingId on Onboarding, a leadId on Sales.

export const PANEL =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

export const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
export const firstOf = (name) => String(name || '').trim().split(/\s+/)[0] || 'them';
export const sameDay = (a, b) => !!a && moment(a).isSame(moment(b), 'day');

// ---- The app's popover (Nic 2026-10-07) ------------------------------------------
// Never the browser's black tooltip. Opens on hover or focus, closes on
// leave, Escape, or a tap outside.
export const POP_CLASS =
  'z-[1075] rounded-xl bg-white p-3 text-left shadow-[0_10px_30px_rgba(15,23,42,0.16),0_2px_6px_rgba(15,23,42,0.08)] ring-1 ring-slate-200 dark:!bg-surface-card dark:ring-white/10';
export const useHoverPop = (width) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const timer = useRef(null);
  const style = useAnchoredPopover(open, btnRef, width, popRef);
  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), 120);
  };
  const hide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => {
      if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);
  return { open, setOpen, show, hide, timer, btnRef, popRef, style };
};

// ---- Counters (they're the filters) ---------------------------------------------

// The board's clock (every Workspace date reads Central, like the Sales board).
const DESK_TZ = 'America/Chicago';

// "Your move today" on a closed day (Nic 2026-11-01): the list underneath is
// the next working day's, so the tile names that day instead of raising a
// Sunday alarm. The hint stays: nothing is locked on a closed day, anyone
// signing in at 6 AM can work the list. closedUntil is the board's next
// opening (workCalendar.nextOpenAt) when today is closed, else null; the
// label reads "today" again from midnight Central on the next working day.
export const yourMoveWords = (closedUntil, hint) => {
  if (!closedUntil) return { label: 'Your move today', hint };
  return { label: `Your move ${moment.tz(closedUntil, DESK_TZ).format('dddd')}`, hint };
};

// "Closed today (Baker Day). The board opens again Monday at 8:00 AM." Names
// a holiday when there is one, because "closed" on a Tuesday is confusing
// without the reason. The Sales board says when it opens again because its
// clocks pause; pass what = null for a Workspace where nothing pauses, and
// the line stops at "Closed today." Shared since 2026-11-01.
export const closedTodayLabel = (workCalendar, what = 'The board', now = new Date()) => {
  const today = moment.tz(now, DESK_TZ);
  const holiday = workCalendar?.daysOff?.[today.format('YYYY-MM-DD')];
  const reason = typeof holiday === 'string' && holiday ? ` (${holiday})` : '';
  const next = what && workCalendar?.nextOpenAt ? ` ${what} opens again ${startsAtText(workCalendar.nextOpenAt, now)}.` : '';
  return `Closed today${reason}.${next}`;
};

// The slate "closed today" line over a Workspace list: being closed is a
// normal state, not a problem. Renders nothing on a working day.
export const ClosedTodayNote = ({ workCalendar, what = null, children = null, now = new Date() }) => {
  if (!workCalendar || workCalendar.isWorkingToday !== false) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:!bg-white/5 dark:text-slate-300">
      <CalendarCog size={12} />
      <span>
        {closedTodayLabel(workCalendar, what, now)}
        {children ? <> {children}</> : null}
      </span>
    </div>
  );
};

export const Counter = ({ value, label, hint, on, tone = '', icon = null, onClick }) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onClick}
    className={cn(
      'flex min-w-0 flex-col items-start rounded-2xl px-4 py-3 text-left outline-none ring-1 transition-[box-shadow,background-color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
      on
        ? 'bg-sky-50 ring-2 ring-sky-500 dark:!bg-sky-500/10 dark:ring-sky-400'
        : 'bg-white ring-slate-200 hover:ring-slate-300 dark:!bg-surface-card dark:ring-white/10 dark:hover:ring-white/20',
    )}
  >
    <span className={cn('text-[26px] font-bold leading-tight tabular-nums text-slate-900 dark:text-white', tone)}>{value}</span>
    <span className="inline-flex max-w-full items-center gap-1 text-[13px] font-semibold text-slate-800 dark:text-slate-100">
      {icon}
      {label}
    </span>
    <span className="w-full min-w-0 truncate text-[11.5px] text-slate-500 dark:text-slate-400">{hint}</span>
  </button>
);

// A filter chip over the list.
export const FilterChip = ({ on, label, n, onClick }) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onClick}
    className={cn(
      'inline-flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-semibold outline-none ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
      on
        ? 'bg-slate-900 text-white ring-slate-900 dark:!bg-slate-100 dark:!text-slate-900 dark:ring-slate-100'
        : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
    )}
  >
    {label}
    <span className="tabular-nums opacity-70">{n}</span>
  </button>
);

// ---- To-do mark beside a person's name (Nic 2026-10-12) -----------------------
// A sticky note when they have open to-dos about this person: sky for their
// own, violet for the page's suggestions (the Today card's colors). Hover
// shows the list.
export const TodoMark = ({ mine = [], suggested = [] }) => {
  const { open, setOpen, show, hide, timer, btnRef, popRef, style } = useHoverPop(264);
  if (!mine.length && !suggested.length) return null;
  const label = [
    mine.length ? `${mine.length} to-do${mine.length === 1 ? '' : 's'} of yours` : null,
    suggested.length ? `${suggested.length} suggested` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const part = 'inline-flex items-center gap-[5px] px-[9px] text-[12px] font-bold tabular-nums';
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`To-dos: ${label}`}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          clearTimeout(timer.current);
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="inline-flex h-[26px] shrink-0 overflow-hidden rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.08)] outline-none ring-1 ring-black/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:ring-white/10"
      >
        {mine.length ? (
          <span className={cn(part, 'bg-sky-100 text-sky-800 dark:!bg-sky-400/15 dark:text-sky-200')}>
            <StickyNote size={14} strokeWidth={2.25} />
            {mine.length}
          </span>
        ) : null}
        {suggested.length ? (
          <span className={cn(part, 'bg-violet-100 text-violet-800 dark:!bg-violet-400/15 dark:text-violet-200')}>
            <Sparkles size={14} strokeWidth={2.25} />
            {suggested.length}
          </span>
        ) : null}
      </button>
      {open && style
        ? createPortal(
            <div
              ref={popRef}
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={hide}
              onClick={(e) => e.stopPropagation()}
              style={{ ...style, overflowY: 'visible', maxHeight: 'none' }}
              className={POP_CLASS}
            >
              {mine.length ? (
                <div>
                  <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-sky-700 dark:text-sky-300">
                    <StickyNote size={11} /> My to-dos
                  </div>
                  <ul className="mb-0 list-none pl-0">
                    {mine.map((x) => (
                      <li key={x.id} className="flex gap-2 py-[3px] text-[12.5px] leading-snug text-slate-700 dark:text-slate-200">
                        <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-sky-500" />
                        <span className="min-w-0 break-words">{x.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {suggested.length ? (
                <div className={cn(mine.length && 'mt-2.5 border-t border-slate-100 pt-2.5 dark:border-white/10')}>
                  <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-violet-700 dark:text-violet-300">
                    <Sparkles size={11} /> Suggested today
                  </div>
                  <ul className="mb-0 list-none pl-0">
                    {suggested.map((x) => (
                      <li key={x.id} className="flex gap-2 py-[3px] text-[12.5px] leading-snug text-slate-700 dark:text-slate-200">
                        <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-violet-500" />
                        <span className="min-w-0">
                          <span className="block">{x.text}</span>
                          {x.sub ? <span className="block text-[11.5px] text-slate-400">{x.sub}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="mt-2.5 text-[11px] text-slate-400">They check themselves off as you do them.</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

// Open to-dos per person, for the mark beside their name: their own apart
// from the page's suggestions not yet checked off.
// The list says when a counter or chip is narrowing it (Nic 2026-10-23):
// a blue outline on the panel (FILTERED_PANEL) and this pill after the
// title, whose x goes back to everyone.
// The search is narrowing the page (Nic 2026-10-21 on Sales, 2026-10-27 on
// Onboarding): said once above the content, on every tab, with a one-click
// clear, so a search left in the box never passes for "nothing to do".
// shown may be null while a server count is loading.
export const SearchFilterBar = ({ q, shown, total, onClear, noun = 'people' }) => {
  if (!q) return null;
  const one = noun === 'people' ? 'person' : noun.replace(/s$/, '');
  return (
    <div
      role="status"
      data-testid="board-filter-bar"
      className="mb-2.5 flex shrink-0 items-center gap-2 rounded-lg border-l-4 border-sky-500 bg-sky-100 px-3 py-2 text-[13px] text-sky-900 shadow-sm ring-1 ring-sky-300 dark:border-sky-400 dark:!bg-sky-500/20 dark:text-sky-100 dark:ring-sky-400/40"
    >
      <Search size={15} strokeWidth={2.6} className="shrink-0 text-sky-600 dark:text-sky-300" />
      <span className="min-w-0 truncate">
        Filtered by <span className="font-semibold">“{q}”</span>
        <span className="mx-1.5 text-sky-500 dark:text-sky-400">·</span>
        Showing {shown == null ? '…' : shown} of {total ?? 0} {total === 1 ? one : noun} here
      </span>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 dark:!bg-sky-500 dark:hover:!bg-sky-400 dark:text-slate-900"
      >
        Clear filter
        <X size={12} strokeWidth={2.6} />
      </button>
    </div>
  );
};

export const FILTERED_PANEL = 'ring-2 ring-sky-400 dark:ring-sky-400/60';
export const FilteredPill = ({ onClear }) => (
  <button
    type="button"
    onClick={onClear}
    title="Show everyone"
    className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 outline-none hover:bg-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-sky-500/15 dark:text-sky-300 dark:hover:!bg-sky-500/25"
  >
    Filtered
    <X size={11} strokeWidth={2.5} />
  </button>
);

// Done on its own (Nic 2026-11-03): a suggestion carries its own `done`,
// worked out from the fact it is about (the nudge went out, the meeting was
// met, the facts were confirmed) by the page that builds it. Nothing is
// stored and nothing is ticked by hand: the Suggested list has no box. The
// old render-diff auto-check (useAutoDone, 2026-10-30) and its saved
// autoKey rows are gone; My to-dos stay the person's own, ticked by hand.
export const todosByPerson = (todos, suggestions) => {
  const out = { mine: new Map(), suggested: new Map() };
  const pushTo = (m, id, x) => m.set(id, [...(m.get(id) || []), x]);
  todos
    .filter((t) => !t.autoKey && !t.done && t.personId)
    .forEach((t) => pushTo(out.mine, t.personId, { id: t.todoId, text: t.text }));
  suggestions
    .filter((x) => !x.done)
    .forEach((x) => pushTo(out.suggested, x.personId, { id: x.key, text: x.text, sub: x.why }));
  return out;
};

// ---- Person picker (Nic 2026-10-11: the native select looked sharp and
// old) ---------------------------------------------------------------------------
// A soft button that opens a rounded panel above it: search, then the
// people with their initials. Arrow keys, Enter and Escape work.
//   people: [{ id, name }]  words: { noun: 'client' | 'lead' }
export const PersonPicker = ({ people, value, onChange, noun = 'client', idPrefix = 'desk' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const picked = people.find((c) => c.id === value) || null;
  const list = [{ id: '', name: `No ${noun}` }, ...people].filter(
    (c) => !q.trim() || (c.id && String(c.name).toLowerCase().includes(q.trim().toLowerCase())),
  );
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const choose = (c) => {
    onChange(c.id);
    setOpen(false);
    setQ('');
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(h + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (list[hi]) choose(list[hi]);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };
  return (
    <div ref={ref} className="relative min-w-0 flex-1">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setHi(0);
        }}
        className="flex h-8 w-full items-center gap-2 rounded-lg bg-slate-50 px-2 text-left text-[12.5px] outline-none ring-1 ring-slate-200 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-white/5 dark:ring-white/10 dark:hover:!bg-white/10"
      >
        {picked ? (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-[9.5px] font-bold text-sky-700 dark:text-sky-300">
            {initialsOf(picked.name)}
          </span>
        ) : null}
        <span className={cn('min-w-0 flex-1 truncate', picked ? 'font-semibold text-slate-700 dark:text-slate-200' : 'text-slate-400')}>
          {picked ? picked.name : `About a ${noun} (optional)`}
        </span>
        <ChevronDown size={13} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-xl bg-white shadow-[0_4px_12px_rgba(15,23,42,0.08),0_16px_40px_rgba(15,23,42,0.16)] ring-1 ring-slate-200/70 dark:!bg-surface-elevated dark:ring-white/10">
          <div className="border-b border-slate-100 p-2 dark:border-white/10">
            <div className="flex h-8 items-center gap-2 rounded-lg bg-slate-50 px-2 dark:!bg-white/5">
              <Search size={13} className="shrink-0 text-slate-400" />
              <label htmlFor={`${idPrefix}-todo-person-search`} className="sr-only">Find a {noun}</label>
              <input
                id={`${idPrefix}-todo-person-search`}
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setHi(0);
                }}
                onKeyDown={onKey}
                placeholder={`Find a ${noun}`}
                className="h-full min-w-0 flex-1 bg-transparent text-[12.5px] text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200"
              />
            </div>
          </div>
          <ul role="listbox" aria-label={`${noun[0].toUpperCase()}${noun.slice(1)}s`} className="os-scroll mb-0 max-h-60 list-none overflow-y-auto p-1 pl-1">
            {list.length ? (
              list.map((c, i) => {
                const on = c.id === (value || '');
                return (
                  <li key={c.id || 'none'} role="option" aria-selected={on}>
                    <button
                      type="button"
                      onMouseEnter={() => setHi(i)}
                      onClick={() => choose(c)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] outline-none focus:outline-none',
                        i === hi ? 'bg-slate-100 dark:!bg-white/10' : '',
                      )}
                    >
                      {c.id ? (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-[10px] font-bold text-sky-700 dark:text-sky-300">
                          {initialsOf(c.name)}
                        </span>
                      ) : (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:!bg-white/10">
                          <X size={12} />
                        </span>
                      )}
                      <span className={cn('min-w-0 flex-1 truncate', c.id ? 'font-medium text-slate-700 dark:text-slate-200' : 'text-slate-500')}>
                        {c.name}
                      </span>
                      {on ? <Check size={13} className="shrink-0 text-sky-600 dark:text-sky-300" /> : null}
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="px-2 py-3 text-center text-[12px] text-slate-400">No {noun} by that name.</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

// ---- Today: the page's suggestions plus their own ------------------------------

// The one check box both lists use, on the left of each row.
export const DoneBox = ({ done, text, onToggle, className }) => (
  <button
    type="button"
    aria-label={done ? `Not done: ${text}` : `Done: ${text}`}
    title={done ? 'Mark not done' : 'Mark done'}
    onClick={onToggle}
    className={cn(
      'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
      done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white hover:border-sky-500 dark:border-white/25 dark:!bg-transparent',
      className,
    )}
  >
    {done ? <Check size={12} strokeWidth={3} /> : null}
  </button>
);

/**
 * Today (Nic 2026-10-12): the page's suggestions and their own to-dos.
 *   suggestions: [{ key, personId, text, why, done }]; the key's prefix
 *     (before the first ':') picks its icon from `kinds`. `done` is the
 *     page's own reading of the fact (Nic 2026-11-03): nothing here ticks a
 *     suggestion, it checks itself off.
 *   todos: [{ todoId, text, done, personId, personName, createdAt }] (rows
 *     carrying an autoKey are the old auto-check's and are ignored)
 *   onAdd({ text, autoKey, personId, done }, { onSuccess }), onUpdate({ todoId,
 *   done }), onRemove({ todoId }), onSaveHideDone(next).
 *   people: the picker's [{ id, name }]; noun: 'client' | 'lead'.
 */
export const TodayCard = ({
  suggestions,
  kinds,
  todos: mine = [],
  adding = false,
  onAdd,
  onUpdate,
  onRemove,
  canAct,
  people,
  noun = 'client',
  idPrefix = 'desk',
  onOpen,
  now,
  savedHideDone = false,
  onSaveHideDone,
}) => {
  const [text, setText] = useState('');
  const [personId, setPersonId] = useState('');
  // Two groups (Nic 2026-10-12: what they add must read apart from what the
  // page adds). Suggested: the page's, by kind, the done ones stay where
  // they are, struck through, like a paper checklist (Nic 2026-10-21).
  // Mine: their own, with the person they're about.
  const suggested = suggestions.map((x) => ({
    id: x.key,
    text: x.text,
    sub: x.why,
    personId: x.personId,
    done: !!x.done,
    autoKey: x.key,
  }));
  const own = mine
    .filter((t) => !t.autoKey)
    .map((t) => ({ id: t.todoId, todoId: t.todoId, text: t.text, personId: t.personId, personName: t.personName, done: t.done, createdAt: t.createdAt }))
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const all = [...suggested, ...own];
  const doneCount = all.filter((x) => x.done).length;
  const left = all.length - doneCount;
  // Hide done (Nic 2026-10-12): one click, saved with the person (it follows
  // them to any browser). It flips at once; the save follows.
  const [hideDone, setHideDone] = useState(!!savedHideDone);
  useEffect(() => setHideDone(!!savedHideDone), [savedHideDone]);
  const flipHideDone = () => {
    const next = !hideDone;
    setHideDone(next);
    onSaveHideDone?.(next);
  };
  const visible = (list) => (hideDone ? list.filter((x) => !x.done) : list);
  // Only their own to-dos tick (Nic 2026-11-03): a suggestion checks itself off.
  const toggle = (x) => onUpdate({ todoId: x.todoId, done: !x.done });
  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAdd(
      { text: text.trim(), personId: personId || null },
      {
        onSuccess: () => {
          setText('');
          setPersonId('');
        },
      },
    );
  };
  const pct = all.length ? Math.round((doneCount / all.length) * 100) : 0;
  const shownSuggested = visible(suggested);
  const shownOwn = visible(own);

  return (
    <section className={cn(PANEL, 'p-4')} aria-label="Today">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <h3 className="mb-0 text-[14px] font-bold text-slate-800 dark:text-slate-100">Today</h3>
          <p className="mb-0 text-[12px] text-slate-400">{moment(now).format('dddd, MMM D')}</p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {doneCount ? (
            <button
              type="button"
              onClick={flipHideDone}
              aria-pressed={hideDone}
              className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-lg px-2 text-[11.5px] font-semibold text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-400 dark:hover:!bg-white/10 dark:hover:text-white"
            >
              {hideDone ? <Eye size={13} /> : <EyeOff size={13} />}
              {hideDone ? `Show done (${doneCount})` : 'Hide done'}
            </button>
          ) : null}
        </div>
      </div>

      {all.length ? (
        <div className="mt-3 flex items-center gap-2.5">
          <div className="h-[5px] flex-1 overflow-hidden rounded-full bg-slate-100 dark:!bg-white/10">
            <div
              className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="shrink-0 text-[11.5px] font-semibold tabular-nums text-slate-500 dark:text-slate-400">
            {left ? `${doneCount} of ${all.length} done` : 'All done'}
          </span>
        </div>
      ) : null}

      {/* Suggested: the page's, by kind. */}
      <div className="mt-4">
        <div className="mb-1.5 flex items-center gap-1.5">
          <Sparkles size={12} className="text-violet-500 dark:text-violet-300" />
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">Suggested</span>
          <span className="text-[11px] text-slate-400">· automatically checked off</span>
        </div>
        {shownSuggested.length ? (
          <ul className="mb-0 flex list-none flex-col gap-[3px] pl-0">
            {shownSuggested.map((x) => {
              const kind = kinds[String(x.autoKey).split(':')[0]] || Object.values(kinds)[0];
              const Icon = kind.icon;
              // No box (Nic 2026-11-03: nobody ticks a suggestion, it checks
              // itself off); a done one wears a quiet check and the strike.
              // The kind's icon sits on the right (Nic 2026-10-21).
              return (
                <li
                  key={x.id}
                  className="flex items-center gap-2.5 rounded-xl px-1.5 py-[6px] hover:bg-slate-50 dark:hover:!bg-white/[0.04]"
                >
                  {/* Only a done row carries the check (Nic 2026-11-03: the
                      open ones sit flush left, no empty slot). */}
                  {x.done ? (
                    <span
                      aria-label={`Done: ${x.text}`}
                      className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => x.personId && onOpen(x.personId)}
                    className="min-w-0 flex-1 text-left outline-none focus:outline-none"
                  >
                    <span className={cn('block truncate text-[13px] font-semibold', x.done ? 'text-slate-400 line-through' : 'text-slate-800 hover:underline dark:text-slate-100')}>
                      {x.text}
                    </span>
                    {x.sub ? <span className="block truncate text-[11.5px] text-slate-400">{x.sub}</span> : null}
                  </button>
                  <span
                    className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', kind.tile, x.done && 'opacity-50')}
                    aria-hidden="true"
                  >
                    <Icon size={15} strokeWidth={2.25} />
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mb-0 px-1.5 py-1 text-[12.5px] text-slate-400">
            {suggested.length ? 'All suggestions done.' : 'Nothing suggested today.'}
          </p>
        )}
      </div>

      {/* Mine: what they added themselves. */}
      <div className="mt-4 border-t border-slate-100 pt-3 dark:border-white/10">
        <div className="mb-1.5 flex items-center gap-1.5">
          <StickyNote size={12} className="text-sky-600 dark:text-sky-300" />
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">My to-dos</span>
          {own.length ? <span className="text-[11px] tabular-nums text-slate-400">{own.filter((x) => !x.done).length} open</span> : null}
        </div>
        {shownOwn.length ? (
          <ul className="mb-0 flex list-none flex-col gap-[3px] pl-0">
            {shownOwn.map((x) => (
              <li key={x.id} className="group flex items-start gap-2.5 rounded-xl px-1.5 py-[7px] hover:bg-slate-50 dark:hover:!bg-white/[0.04]">
                {canAct ? <DoneBox done={x.done} text={x.text} onToggle={() => toggle(x)} className="mt-[1px]" /> : null}
                <div className="min-w-0 flex-1">
                  <span className={cn('block break-words text-[13px] font-medium leading-snug', x.done ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100')}>
                    {x.text}
                  </span>
                  {x.personName ? (
                    <button
                      type="button"
                      onClick={() => onOpen(x.personId)}
                      className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-full bg-sky-50 py-[2px] pl-[2px] pr-2 text-[11.5px] font-semibold text-sky-800 outline-none hover:bg-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-sky-400/10 dark:text-sky-200 dark:hover:!bg-sky-400/20"
                    >
                      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-sky-600 text-[8.5px] font-bold text-white dark:bg-sky-400 dark:!text-slate-900">
                        {initialsOf(x.personName)}
                      </span>
                      <span className="truncate">{x.personName}</span>
                    </button>
                  ) : null}
                </div>
                {canAct ? (
                  <button
                    type="button"
                    aria-label={`Remove: ${x.text}`}
                    onClick={() => onRemove({ todoId: x.todoId })}
                    className="rounded-md p-1 text-slate-300 opacity-[0] outline-none hover:bg-white hover:text-rose-600 focus:opacity-100 focus:outline-none group-hover:opacity-100 dark:hover:!bg-white/10"
                  >
                    <Trash2 size={13} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : own.length ? (
          <p className="mb-0 px-1.5 py-1 text-[12.5px] text-slate-400">All yours done.</p>
        ) : null}

        {canAct ? (
          <form
            onSubmit={submit}
            className="mt-2 rounded-xl bg-slate-50 p-1.5 ring-1 ring-slate-200/70 focus-within:bg-white focus-within:ring-sky-400/60 dark:!bg-white/[0.04] dark:ring-white/10 dark:focus-within:!bg-white/[0.06]"
          >
            <label htmlFor={`${idPrefix}-todo-text`} className="sr-only">Add a to-do</label>
            <div className="flex items-center gap-1.5 px-1">
              <Plus size={14} className="shrink-0 text-slate-400" />
              <input
                id={`${idPrefix}-todo-text`}
                value={text}
                maxLength={300}
                onChange={(e) => setText(e.target.value)}
                placeholder="Add a to-do"
                className="h-8 w-full bg-transparent text-[13px] text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200"
              />
            </div>
            {text.trim() ? (
              <div className="mt-1 flex gap-[6px]">
                <PersonPicker people={people} value={personId} onChange={setPersonId} noun={noun} idPrefix={idPrefix} />
                <button
                  type="submit"
                  disabled={adding}
                  className="inline-flex h-8 items-center gap-1 rounded-lg bg-slate-900 px-3 text-[12.5px] font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:!bg-slate-100 dark:!text-slate-900"
                >
                  {adding ? <Loader2 size={13} className="animate-spin" /> : null} Add
                </button>
              </div>
            ) : null}
          </form>
        ) : null}
      </div>
    </section>
  );
};

// ---- Schedule: the week, and the day's meetings --------------------------------

// The work week to show: this one, or on a Saturday or Sunday the coming
// one (Nic 2026-10-11: on Sunday it showed last week, since a week starts on
// Monday).
const workWeekOf = (now) => {
  const t = moment(now).startOf('day');
  return t.day() === 0 || t.day() === 6 ? t.clone().add(1, 'week').startOf('isoWeek') : t.clone().startOf('isoWeek');
};

// apiBase: whose meetings ('/client-onboarding' or '/onboarding-board').
// onOpenMeeting(m): a meeting's row was clicked.
export const ScheduleCard = ({ now, scope, apiBase, onOpenMeeting, onOpenCalendar, showOwner, fallbackName = 'Client', fallbackEvent = 'Meeting' }) => {
  const [offset, setOffset] = useState(0);
  const weekStart = workWeekOf(now).add(offset, 'weeks');
  const days = [0, 1, 2, 3, 4].map((i) => weekStart.clone().add(i, 'days'));
  // Today when it's a weekday; else the week's Monday.
  const defaultDay = () => {
    const t = moment(now).startOf('day');
    return t.day() === 0 || t.day() === 6 ? workWeekOf(now).format('YYYY-MM-DD') : t.format('YYYY-MM-DD');
  };
  const [picked, setPicked] = useState(defaultDay);
  // Left open past midnight, it moves to the new today (branch QA 2026-10-21).
  const todayKey = moment(now).format('YYYY-MM-DD');
  const lastToday = useRef(todayKey);
  useEffect(() => {
    if (lastToday.current === todayKey) return;
    lastToday.current = todayKey;
    setOffset(0);
    setPicked(defaultDay());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayKey]);
  const moveWeek = (n) => {
    const next = offset + n;
    setOffset(next);
    setPicked(next === 0 ? defaultDay() : workWeekOf(now).add(next, 'weeks').format('YYYY-MM-DD'));
  };
  const range = useMemo(
    () => ({ from: weekStart.toISOString(), to: weekStart.clone().add(7, 'days').toISOString() }),
    // A new week re-anchors it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStart.format('YYYY-MM-DD')],
  );
  const q = useFetchMeetings({ ...range, scope, apiBase });
  // Their own Outlook appointments sit in the same list (Nic 2026-10-28):
  // all-day ones first, then by time, each with a small Outlook tag.
  const meetings = useMemo(
    () =>
      [
        ...(q.data?.meetings || []).filter((m) => m.status !== 'canceled'),
        ...(q.data?.outlook?.events || []).map((e) => ({ ...e, calendlyBookingId: `outlook:${e.id}`, provider: 'outlook' })),
      ].sort((a, b) => (b.allDay ? 1 : 0) - (a.allDay ? 1 : 0) || new Date(a.startTime) - new Date(b.startTime)),
    [q.data],
  );
  const onDay = (d) => meetings.filter((m) => moment(m.startTime).isSame(d, 'day'));
  const list = onDay(moment(picked));
  // A meeting falls off the list once its end has passed (Nic 2026-11-04:
  // 1:00 to 1:30 is gone at 1:30; the full calendar keeps it). The day's
  // dots still count everything the day had.
  const over = (m) => new Date(m.endTime || m.startTime) <= new Date(now);
  const ahead = list.filter((m) => !over(m));
  return (
    <section className={cn(PANEL, 'p-4')} aria-label="Schedule">
      {/* Two clean rows (Nic 2026-10-11: the one-row header wrapped): the
          title with the full calendar, then the week in its own bar. */}
      <div className="mb-2.5 flex items-center gap-2">
        <h3 className="mb-0 text-[14px] font-bold text-slate-800 dark:text-slate-100">Schedule</h3>
        <button
          type="button"
          onClick={onOpenCalendar}
          title="Open the full calendar"
          aria-label="Open the full calendar"
          className="ml-auto inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-[12px] font-semibold text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-400 dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <CalendarDays size={14} /> Calendar
        </button>
      </div>
      <div className="mb-2.5 flex h-8 items-center gap-1 rounded-xl bg-slate-50 px-1 ring-1 ring-slate-200/70 dark:!bg-white/[0.04] dark:ring-white/10">
        <button
          type="button"
          aria-label="Previous week"
          onClick={() => moveWeek(-1)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-slate-400 outline-none hover:bg-white hover:text-slate-700 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="min-w-0 flex-1 truncate text-center text-[12.5px] font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {weekStart.format('MMM D')} to {weekStart.clone().add(4, 'days').format(weekStart.month() === weekStart.clone().add(4, 'days').month() ? 'D' : 'MMM D')}
        </span>
        {offset ? (
          <button
            type="button"
            onClick={() => moveWeek(-offset)}
            className="h-6 shrink-0 whitespace-nowrap rounded-lg bg-white px-2 text-[11.5px] font-semibold text-sky-700 shadow-sm outline-none hover:text-sky-800 focus:outline-none focus-visible:!shadow-[0_0_0_2px_rgba(56,189,248,0.6)] dark:!bg-white/10 dark:text-sky-300"
          >
            Today
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Next week"
          onClick={() => moveWeek(1)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-slate-400 outline-none hover:bg-white hover:text-slate-700 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="grid grid-cols-5 gap-[4px]">
        {days.map((d) => {
          const key = d.format('YYYY-MM-DD');
          const on = key === picked;
          const n = onDay(d).length;
          const today = d.isSame(moment(now), 'day');
          return (
            <button
              key={key}
              type="button"
              aria-pressed={on}
              onClick={() => setPicked(key)}
              className={cn(
                'flex flex-col items-center rounded-xl py-[6px] text-[11px] outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
                on ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:!bg-white/5 dark:text-slate-400 dark:hover:!bg-white/10',
              )}
            >
              <span className={cn(today && !on && 'font-bold text-sky-700 dark:text-sky-300')}>{d.format('ddd')}</span>
              <span className="text-[15px] font-bold leading-tight">{d.format('D')}</span>
              <span className="mt-[2px] flex h-[5px] gap-[3px]">
                {Array.from({ length: Math.min(n, 3) }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <i key={i} className={cn('block h-[5px] w-[5px] rounded-full', on ? 'bg-white dark:!bg-slate-900' : 'bg-sky-500')} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-3">
        {q.isLoading ? (
          <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
        ) : ahead.length ? (
          <ul className="mb-0 list-none pl-0">
            {ahead.map((m) => {
              // Marked missed (Nic 2026-10-30): greyed with the word, no Join.
              const missed = !!m.noShowAt || m.outcome === 'no_show';
              if (isOutlook(m)) {
                // Not ours to open: the subject (Busy when private), the tag.
                return (
                  <li key={m.calendlyBookingId} className="flex items-center gap-2 border-t border-slate-100 first:border-t-0 dark:border-white/10">
                    <div className="grid min-w-0 flex-1 grid-cols-[4.25rem_minmax(0,1fr)] gap-[10px] py-[8px]">
                      <span className="text-[12px] font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                        {m.allDay ? 'All day' : moment(m.startTime).format('h:mm A')}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] font-semibold text-slate-600 dark:text-slate-300">
                          {m.subject || 'Busy'}
                        </span>
                        {/* A line box tall enough for the tag's ring (Nic 2026-11-03: it was clipped). */}
                        <span className="block truncate text-[12px] leading-[20px] text-slate-400">
                          <OutlookTag />
                        </span>
                      </span>
                    </div>
                  </li>
                );
              }
              return (
                <li key={m.calendlyBookingId} className="flex items-center gap-2 border-t border-slate-100 first:border-t-0 dark:border-white/10">
                  <button
                    type="button"
                    onClick={() => onOpenMeeting(m)}
                    className="grid min-w-0 flex-1 grid-cols-[4.25rem_minmax(0,1fr)] gap-[10px] py-[8px] text-left outline-none focus:outline-none"
                  >
                    <span className={cn('text-[12px] font-semibold tabular-nums', missed ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200')}>
                      {moment(m.startTime).format('h:mm A')}
                    </span>
                    <span className="min-w-0">
                      <span className={cn('block truncate text-[13px] font-semibold hover:underline', missed ? 'text-slate-400' : 'text-slate-800 dark:text-slate-100')}>
                        {`${m.leadFirstName || ''} ${m.leadLastName || ''}`.trim() || fallbackName}
                      </span>
                      <span className="block truncate text-[12px] text-slate-400">
                        {m.eventTypeName || fallbackEvent}
                        {showOwner && m.hostName ? ` · ${firstOf(m.hostName)}` : ''}
                        {missed ? <span className="font-semibold text-rose-500 dark:text-rose-300"> · missed</span> : ''}
                        {isLegacy(m) && !missed ? <LegacyTag className="ml-1.5" /> : null}
                      </span>
                    </span>
                  </button>
                  {/* A video meeting that can be joined now (Nic 2026-10-27):
                      the room opens in a new tab, no need to open the client. */}
                  {!missed && joinable(m, new Date(now).getTime()) ? (
                    <a
                      href={m.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Join the video meeting with ${`${m.leadFirstName || ''} ${m.leadLastName || ''}`.trim() || fallbackName}`}
                      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md bg-sky-600 px-2 text-[12px] font-semibold text-white no-underline hover:bg-sky-500 hover:text-white"
                    >
                      <Video size={12} /> Join
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="py-3 text-center text-[12.5px] text-slate-400">
            {list.length ? 'All done' : 'No meetings'} {sameDay(picked, now) ? 'today' : moment(picked).format('dddd')}.
            {q.data?.outlook?.configured && !q.data.outlook.checked ? <span className="block text-[11.5px]">Outlook couldn't be checked.</span> : null}
          </div>
        )}
      </div>
    </section>
  );
};

// ---- The list's pieces -----------------------------------------------------------

// A sortable column head. sort: { key, dir }; 'attention' is the desk's
// own order (one more click on a column goes back to it).
export const SortHead = ({ sortKey, label, sort, setSort }) => {
  const on = sort.key === sortKey;
  const Icon = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className="px-3 py-2.5 text-left">
      <button
        type="button"
        onClick={() =>
          setSort((s) =>
            s.key === sortKey ? (s.dir === 'desc' ? { key: sortKey, dir: 'asc' } : { key: 'attention', dir: 'desc' }) : { key: sortKey, dir: 'desc' },
          )
        }
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-bold uppercase tracking-wide outline-none focus:outline-none',
          on ? 'text-sky-700 dark:text-sky-300' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200',
        )}
      >
        {label}
        {on ? <Icon size={11} /> : null}
      </button>
    </th>
  );
};

export const PlainHead = ({ children, className }) => (
  <th className={cn('px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400', className)}>{children}</th>
);

// The row's star (Nic 2026-10-12): their own quick list.
export const PinStar = ({ pinned, name, onFlip }) => (
  <button
    type="button"
    aria-pressed={pinned}
    aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
    title={pinned ? 'Unpin' : 'Pin to your quick list'}
    onClick={(e) => {
      e.stopPropagation();
      onFlip();
    }}
    onKeyDown={(e) => e.stopPropagation()}
    className={cn(
      'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg outline-none transition-[color,opacity,transform] duration-150 hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 active:scale-90 dark:hover:!bg-amber-400/10',
      pinned
        ? 'text-amber-400'
        : // opacity-[0], not opacity-0: Bootstrap's .opacity-0 is !important and beat the hover.
          'text-slate-400 opacity-[0] hover:text-amber-400 focus-visible:opacity-100 group-hover/row:opacity-100 [@media(hover:none)]:opacity-50',
    )}
  >
    <Star size={15} strokeWidth={2} className={cn(pinned && 'fill-amber-400')} />
  </button>
);

// The notes button at the row's end: opens them on their notes.
export const NotesButton = ({ name, onClick }) => (
  <button
    type="button"
    aria-label={`Open ${name || 'their'} notes`}
    title="Open their notes"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    onKeyDown={(e) => e.stopPropagation()}
    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none ring-1 ring-amber-200/80 transition-transform duration-150 hover:-translate-y-px hover:bg-amber-100 hover:text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 active:scale-95 dark:!bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/25 dark:hover:!bg-amber-400/20"
  >
    <StickyNote size={15} strokeWidth={2.25} />
  </button>
);
